import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronRight,
  IconCoin,
  IconDots,
  IconPencil,
  IconPlayerStop,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react"
import { useState } from "react"

interface AvailableUser {
  id: number
  name: string
  avatar_url: string | null
}

interface RateRow {
  id: number
  hourly_rate: number
  hours_per_month: number
  discount_percentage: number
  effective_date: string
  end_date: string | null
  change_reason: string | null
}

interface AssignmentRow {
  id: number
  user: { id: number; name: string; avatar_url: string | null }
  started_on: string | null
  ended_on: string | null
  close_reason: string | null
  open: boolean
  current_rate: RateRow | null
  rates: RateRow[]
}

interface PaymentScheduleRow {
  id: number
  amount: number
  cadence: string
  custom_days_interval: number | null
  effective_date: string
  end_date: string | null
  change_reason: string | null
}

interface DiscountRow {
  id: number
  discount_type: string
  value: number
  applies_to: string
  effective_date: string
  end_date: string | null
  change_reason: string | null
}

interface Project {
  id: number
  name: string
  key: string | null
  description: string | null
  project_type: string
  display_project_type: string
  status: string
  start_date: string | null
  end_date: string | null
  customer: { id: number; name: string }
  hour_cap: number | null
  default_hourly_rate: number | null
  assignments: AssignmentRow[]
  payment_schedules: PaymentScheduleRow[]
  discounts: DiscountRow[]
  finance_summary: {
    monthly_revenue: number
    monthly_employee_cost: number
    monthly_profit: number
    total_assigned_hours: number
  }
  can_update: boolean
}

interface Props {
  [key: string]: unknown
  project: Project
  available_users: AvailableUser[]
  can_manage_team: boolean
  can_manage_finance: boolean
}

const STATUS_BADGE: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; label: string }> = {
  active: { variant: "default", label: "Active" },
  in_progress: { variant: "default", label: "In Progress" },
  planning: { variant: "secondary", label: "Planning" },
  completed: { variant: "outline", label: "Completed" },
  on_hold: { variant: "destructive", label: "On Hold" },
  cancelled: { variant: "outline", label: "Cancelled" },
}

const fmt = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" })
const fmtDate = (s: string | null) =>
  s ? new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"
const today = () => new Date().toISOString().slice(0, 10)

export default function ProjectShow() {
  const { project, available_users, can_manage_team, can_manage_finance } = usePage<Props>().props
  const isTM = project.project_type === "time_and_materials"
  const isFixed = project.project_type === "fixed_price"
  const badge = STATUS_BADGE[project.status] || { variant: "outline" as const, label: project.status }

  return (
    <>
      <Head title={`${project.name} - Operations Center`} />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="space-y-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/">Home</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="/projects">Projects</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{project.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                <a href="/projects">
                  <IconArrowLeft className="h-4 w-4" />
                </a>
              </Button>
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-lg font-semibold tracking-tight">{project.name}</h1>
                  <Badge variant={badge.variant}>{badge.label}</Badge>
                  <Badge variant="outline">{project.display_project_type}</Badge>
                </div>
                <p className="text-muted-foreground text-sm">
                  <a href={`/customers/${project.customer.id}`} className="hover:text-foreground transition-colors">
                    {project.customer.name}
                  </a>
                  {project.start_date && (
                    <>
                      {" "}
                      &middot; {fmtDate(project.start_date)}
                      {project.end_date ? ` – ${fmtDate(project.end_date)}` : " – present"}
                    </>
                  )}
                  {project.key && (
                    <>
                      {" "}
                      &middot; <code className="text-xs">{project.key}</code>
                    </>
                  )}
                </p>
              </div>
            </div>
            {project.can_update && (
              <Button size="sm" asChild>
                <a href={`/projects/${project.id}/edit`}>
                  <IconPencil className="mr-1.5 h-4 w-4" />
                  Edit project
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Finance summary cards */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <SummaryCard label="Monthly revenue" value={fmt(project.finance_summary.monthly_revenue)} />
          <SummaryCard label="Monthly cost" value={fmt(project.finance_summary.monthly_employee_cost)} />
          <SummaryCard
            label="Monthly margin"
            value={fmt(project.finance_summary.monthly_profit)}
            tone={project.finance_summary.monthly_profit >= 0 ? "positive" : "negative"}
          />
          <SummaryCard label="Hours / mo" value={String(project.finance_summary.total_assigned_hours)} />
        </div>

        {project.description && (
          <div className="rounded-xl border bg-card p-5">
            <p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wider">Description</p>
            <p className="text-sm leading-relaxed">{project.description}</p>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue={isTM ? "team" : "schedule"} className="w-full">
          <TabsList>
            <TabsTrigger value="team">Team & rates</TabsTrigger>
            {isFixed && <TabsTrigger value="schedule">Payment schedule</TabsTrigger>}
            <TabsTrigger value="discounts">Discounts</TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-6">
            <TeamSection
              project={project}
              availableUsers={available_users}
              canManageTeam={can_manage_team}
              canManageFinance={can_manage_finance}
              isTM={isTM}
            />
          </TabsContent>

          {isFixed && (
            <TabsContent value="schedule" className="mt-6">
              <PaymentScheduleSection project={project} canManage={can_manage_finance} />
            </TabsContent>
          )}

          <TabsContent value="discounts" className="mt-6">
            <DiscountsSection project={project} canManage={can_manage_finance} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
}

function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: "positive" | "negative"
}) {
  const toneClass =
    tone === "positive" ? "text-emerald-600 dark:text-emerald-400" : tone === "negative" ? "text-destructive" : ""
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className={`text-2xl font-semibold tabular-nums ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  )
}

/* ======================================================================
   TEAM & RATES
   ====================================================================== */

function TeamSection({
  project,
  availableUsers,
  canManageTeam,
  canManageFinance,
  isTM,
}: {
  project: Project
  availableUsers: AvailableUser[]
  canManageTeam: boolean
  canManageFinance: boolean
  isTM: boolean
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Team ({project.assignments.filter((a) => a.open).length} active)</CardTitle>
        {canManageTeam && availableUsers.length > 0 && (
          <AddAssignmentDialog projectId={project.id} availableUsers={availableUsers} requireRate={isTM} />
        )}
      </CardHeader>
      <CardContent>
        {project.assignments.length === 0 ? (
          <p className="text-muted-foreground text-center text-sm py-8">No team members assigned yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8" />
                <TableHead>Member</TableHead>
                <TableHead>Period</TableHead>
                {isTM && (
                  <>
                    <TableHead className="text-right">Rate</TableHead>
                    <TableHead className="text-right">Hrs/mo</TableHead>
                    <TableHead className="text-right">Disc</TableHead>
                  </>
                )}
                <TableHead>Status</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {project.assignments.map((a) => {
                const isExpanded = expandedId === a.id
                return (
                  <>
                    <TableRow key={a.id}>
                      <TableCell>
                        {a.rates.length > 0 && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={() => setExpandedId(isExpanded ? null : a.id)}
                            aria-label="Toggle rate history"
                          >
                            {isExpanded ? (
                              <IconChevronDown className="h-4 w-4" />
                            ) : (
                              <IconChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={a.user.avatar_url || undefined} alt={a.user.name} />
                            <AvatarFallback className="text-xs">{a.user.name.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{a.user.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground tabular-nums">
                        {fmtDate(a.started_on)}
                        {a.ended_on ? ` – ${fmtDate(a.ended_on)}` : " – present"}
                      </TableCell>
                      {isTM && (
                        <>
                          <TableCell className="text-right tabular-nums">
                            {a.current_rate ? fmt(a.current_rate.hourly_rate) : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {a.current_rate ? a.current_rate.hours_per_month : "—"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {a.current_rate && a.current_rate.discount_percentage > 0
                              ? `${a.current_rate.discount_percentage}%`
                              : "—"}
                          </TableCell>
                        </>
                      )}
                      <TableCell>
                        <Badge variant={a.open ? "default" : "outline"}>{a.open ? "Active" : "Ended"}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {a.open && (canManageFinance || canManageTeam) && (
                          <AssignmentRowActions
                            projectId={project.id}
                            assignment={a}
                            isTM={isTM}
                            canManageFinance={canManageFinance}
                            canManageTeam={canManageTeam}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && a.rates.length > 0 && (
                      <TableRow key={`${a.id}-history`}>
                        <TableCell colSpan={isTM ? 8 : 5} className="bg-muted/30 p-0">
                          <RateHistory rates={a.rates} />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function RateHistory({ rates }: { rates: RateRow[] }) {
  const sorted = [...rates].sort((a, b) => b.effective_date.localeCompare(a.effective_date))
  return (
    <div className="px-6 py-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">Rate history</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Effective</TableHead>
            <TableHead>Through</TableHead>
            <TableHead className="text-right">Rate</TableHead>
            <TableHead className="text-right">Hrs/mo</TableHead>
            <TableHead className="text-right">Discount</TableHead>
            <TableHead>Reason</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="tabular-nums">{fmtDate(r.effective_date)}</TableCell>
              <TableCell className="tabular-nums">{r.end_date ? fmtDate(r.end_date) : "current"}</TableCell>
              <TableCell className="text-right tabular-nums">{fmt(r.hourly_rate)}</TableCell>
              <TableCell className="text-right tabular-nums">{r.hours_per_month}</TableCell>
              <TableCell className="text-right tabular-nums">
                {r.discount_percentage > 0 ? `${r.discount_percentage}%` : "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">{r.change_reason ?? "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AddAssignmentDialog({
  projectId,
  availableUsers,
  requireRate,
}: {
  projectId: number
  availableUsers: AvailableUser[]
  requireRate: boolean
}) {
  const [open, setOpen] = useState(false)
  const [userId, setUserId] = useState("")
  const [startedOn, setStartedOn] = useState(today())
  const [hourlyRate, setHourlyRate] = useState("")
  const [hoursPerMonth, setHoursPerMonth] = useState("")
  const [discount, setDiscount] = useState("")
  const [reason, setReason] = useState("Initial rate")
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = userId && startedOn && (!requireRate || (hourlyRate && hoursPerMonth))

  function reset() {
    setUserId("")
    setStartedOn(today())
    setHourlyRate("")
    setHoursPerMonth("")
    setDiscount("")
    setReason("Initial rate")
  }

  function submit() {
    setSubmitting(true)
    const payload: Record<string, unknown> = {
      user_id: userId,
      started_on: startedOn,
    }
    if (hourlyRate && hoursPerMonth) {
      payload.initial_rate = {
        hourly_rate: hourlyRate,
        hours_per_month: hoursPerMonth,
        discount_percentage: discount || "0",
        change_reason: reason,
      }
    }
    router.post(`/projects/${projectId}/project_assignments`, { project_assignment: payload } as never, {
      onSuccess: () => {
        setOpen(false)
        reset()
      },
      onFinish: () => setSubmitting(false),
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <IconPlus className="mr-1.5 h-4 w-4" />
          Add member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add team member</DialogTitle>
          <DialogDescription>
            {requireRate ? "Pick a user, set the start date and their billing rate." : "Pick a user and a start date."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>User</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a user..." />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((u) => (
                  <SelectItem key={u.id} value={String(u.id)}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Start date</Label>
            <Input type="date" value={startedOn} onChange={(e) => setStartedOn(e.target.value)} />
          </div>
          {requireRate && (
            <>
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Hourly rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(e.target.value)}
                    placeholder="75.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hours / mo</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    max="200"
                    value={hoursPerMonth}
                    onChange={(e) => setHoursPerMonth(e.target.value)}
                    placeholder="160"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>
                  Discount % <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Input value={reason} onChange={(e) => setReason(e.target.value)} />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? "Adding..." : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function UpdateRateDialog({
  projectId,
  assignment,
  open,
  onOpenChange,
}: {
  projectId: number
  assignment: AssignmentRow
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [effectiveDate, setEffectiveDate] = useState(today())
  const [hourlyRate, setHourlyRate] = useState(assignment.current_rate?.hourly_rate.toString() ?? "")
  const [hoursPerMonth, setHoursPerMonth] = useState(assignment.current_rate?.hours_per_month.toString() ?? "")
  const [discount, setDiscount] = useState(
    assignment.current_rate?.discount_percentage ? String(assignment.current_rate.discount_percentage) : "",
  )
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  function submit() {
    setSubmitting(true)
    router.post(
      `/projects/${projectId}/project_assignments/${assignment.id}/rates`,
      {
        project_assignment_rate: {
          hourly_rate: hourlyRate,
          hours_per_month: hoursPerMonth,
          discount_percentage: discount || "0",
          effective_date: effectiveDate,
          change_reason: reason,
        },
      },
      {
        onSuccess: () => onOpenChange(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Update rate — {assignment.user.name}</DialogTitle>
          <DialogDescription>This appends a new rate version. The previous rate stays in history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Effective from</Label>
            <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Hourly rate</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Hours / mo</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="200"
                value={hoursPerMonth}
                onChange={(e) => setHoursPerMonth(e.target.value)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Discount %</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Reason for change</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Promotion to senior" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !hourlyRate || !hoursPerMonth || !effectiveDate || !reason}>
            {submitting ? "Saving..." : "Save new rate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EndAssignmentDialog({
  projectId,
  assignment,
  open,
  onOpenChange,
}: {
  projectId: number
  assignment: AssignmentRow
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [endedOn, setEndedOn] = useState(today())
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  function submit() {
    setSubmitting(true)
    router.patch(
      `/projects/${projectId}/project_assignments/${assignment.id}/close`,
      { ended_on: endedOn, close_reason: reason },
      {
        onSuccess: () => onOpenChange(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>End assignment — {assignment.user.name}</DialogTitle>
          <DialogDescription>
            Close out this stint. Member can be re-added later as a new stint with a fresh rate chain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>End date</Label>
            <Input type="date" value={endedOn} onChange={(e) => setEndedOn(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Rolled off to other project"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={submitting || !endedOn}>
            {submitting ? "Ending..." : "End assignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function AssignmentRowActions({
  projectId,
  assignment,
  isTM,
  canManageFinance,
  canManageTeam,
}: {
  projectId: number
  assignment: AssignmentRow
  isTM: boolean
  canManageFinance: boolean
  canManageTeam: boolean
}) {
  const [rateOpen, setRateOpen] = useState(false)
  const [endOpen, setEndOpen] = useState(false)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Row actions">
            <IconDots className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canManageFinance && isTM && (
            <DropdownMenuItem onSelect={() => setRateOpen(true)}>
              <IconCoin className="mr-2 h-4 w-4" />
              Update rate
            </DropdownMenuItem>
          )}
          {canManageFinance && isTM && canManageTeam && <DropdownMenuSeparator />}
          {canManageTeam && (
            <DropdownMenuItem variant="destructive" onSelect={() => setEndOpen(true)}>
              <IconPlayerStop className="mr-2 h-4 w-4" />
              End assignment
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {canManageFinance && isTM && (
        <UpdateRateDialog projectId={projectId} assignment={assignment} open={rateOpen} onOpenChange={setRateOpen} />
      )}
      {canManageTeam && (
        <EndAssignmentDialog projectId={projectId} assignment={assignment} open={endOpen} onOpenChange={setEndOpen} />
      )}
    </>
  )
}

/* ======================================================================
   PAYMENT SCHEDULES
   ====================================================================== */

function PaymentScheduleSection({ project, canManage }: { project: Project; canManage: boolean }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Payment schedule</CardTitle>
        {canManage && <AddPaymentScheduleDialog projectId={project.id} />}
      </CardHeader>
      <CardContent>
        {project.payment_schedules.length === 0 ? (
          <p className="text-muted-foreground text-center text-sm py-8">
            No payment schedules yet. Add one to start booking expected revenue.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Cadence</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Through</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {project.payment_schedules.map((s) => {
                const active = !s.end_date || s.end_date >= today()
                return (
                  <TableRow key={s.id}>
                    <TableCell className="text-right tabular-nums font-medium">{fmt(s.amount)}</TableCell>
                    <TableCell className="capitalize">
                      {s.cadence === "custom_days" ? `Every ${s.custom_days_interval} days` : s.cadence}
                    </TableCell>
                    <TableCell className="tabular-nums">{fmtDate(s.effective_date)}</TableCell>
                    <TableCell className="tabular-nums">{s.end_date ? fmtDate(s.end_date) : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{s.change_reason ?? "—"}</TableCell>
                    <TableCell>
                      {canManage && active && (
                        <DeleteRowButton url={`/projects/${project.id}/payment_schedules/${s.id}`} />
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function AddPaymentScheduleDialog({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState("")
  const [cadence, setCadence] = useState("monthly")
  const [customDays, setCustomDays] = useState("")
  const [effectiveDate, setEffectiveDate] = useState(today())
  const [endDate, setEndDate] = useState("")
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const canSubmit = amount && effectiveDate && (cadence !== "custom_days" || customDays)

  function submit() {
    setSubmitting(true)
    router.post(
      `/projects/${projectId}/payment_schedules`,
      {
        payment_schedule: {
          amount,
          cadence,
          custom_days_interval: cadence === "custom_days" ? customDays : null,
          effective_date: effectiveDate,
          end_date: endDate || null,
          change_reason: reason,
        },
      },
      {
        onSuccess: () => setOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <IconPlus className="mr-1.5 h-4 w-4" />
          Add schedule
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add payment schedule</DialogTitle>
          <DialogDescription>
            Adding a new schedule with a later effective date supersedes the active one (closes its end date).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="12000"
              />
            </div>
            <div className="space-y-2">
              <Label>Cadence</Label>
              <Select value={cadence} onValueChange={setCadence}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="custom_days">Every N days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {cadence === "custom_days" && (
            <div className="space-y-2">
              <Label>Days between payments</Label>
              <Input
                type="number"
                min="1"
                value={customDays}
                onChange={(e) => setCustomDays(e.target.value)}
                placeholder="10"
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Effective from</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                End date <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Initial schedule" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!canSubmit || submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/* ======================================================================
   DISCOUNTS
   ====================================================================== */

function DiscountsSection({ project, canManage }: { project: Project; canManage: boolean }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Discounts</CardTitle>
        {canManage && <AddDiscountDialog projectId={project.id} />}
      </CardHeader>
      <CardContent>
        {project.discounts.length === 0 ? (
          <p className="text-muted-foreground text-center text-sm py-8">No project-level discounts.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Value</TableHead>
                <TableHead>Applies to</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Through</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {project.discounts.map((d) => {
                const active = !d.end_date || d.end_date >= today()
                return (
                  <TableRow key={d.id}>
                    <TableCell className="capitalize">{d.discount_type.replace("_", " ")}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {d.discount_type === "percentage" ? `${d.value}%` : fmt(d.value)}
                    </TableCell>
                    <TableCell className="capitalize">{d.applies_to.replace(/_/g, " ")}</TableCell>
                    <TableCell className="tabular-nums">{fmtDate(d.effective_date)}</TableCell>
                    <TableCell className="tabular-nums">{d.end_date ? fmtDate(d.end_date) : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.change_reason ?? "—"}</TableCell>
                    <TableCell>
                      {canManage && active && <DeleteRowButton url={`/projects/${project.id}/discounts/${d.id}`} />}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}

function AddDiscountDialog({ projectId }: { projectId: number }) {
  const [open, setOpen] = useState(false)
  const [discountType, setDiscountType] = useState("percentage")
  const [value, setValue] = useState("")
  const [appliesTo, setAppliesTo] = useState("all_revenue")
  const [effectiveDate, setEffectiveDate] = useState(today())
  const [endDate, setEndDate] = useState("")
  const [reason, setReason] = useState("")
  const [submitting, setSubmitting] = useState(false)

  function submit() {
    setSubmitting(true)
    router.post(
      `/projects/${projectId}/discounts`,
      {
        discount: {
          discount_type: discountType,
          value,
          applies_to: appliesTo,
          effective_date: effectiveDate,
          end_date: endDate || null,
          change_reason: reason,
        },
      },
      {
        onSuccess: () => setOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <IconPlus className="mr-1.5 h-4 w-4" />
          Add discount
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add discount</DialogTitle>
          <DialogDescription>
            Discounts apply on top of computed revenue. A new entry of the same scope supersedes the active one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={discountType} onValueChange={setDiscountType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="fixed_amount">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{discountType === "percentage" ? "Percent" : "Amount (USD)"}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={discountType === "percentage" ? "10" : "500"}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Applies to</Label>
            <Select value={appliesTo} onValueChange={setAppliesTo}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_revenue">All revenue</SelectItem>
                <SelectItem value="fixed_price_only">Fixed-price revenue only</SelectItem>
                <SelectItem value="t_and_m_only">T&amp;M revenue only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Effective from</Label>
              <Input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>
                End date <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Reason</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Q3 promo" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!value || !effectiveDate || submitting}>
            {submitting ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function DeleteRowButton({ url }: { url: string }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 text-muted-foreground hover:text-destructive"
      onClick={() => {
        if (confirm("Delete this entry?")) router.delete(url)
      }}
    >
      <IconTrash className="h-4 w-4" />
    </Button>
  )
}

ProjectShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
